import React, { ReactNode, useEffect, useState } from "react";
import { DocumentNode, useQuery } from "@apollo/client";
import { AlertMessage, AlertMessageBanner, VpdScrollable } from "@design-system";
import { DataImportHeader } from "./data-import-header";
import {
  DataFrameImportResult,
  DataImportForm,
  DataImportResult,
  FileFormat,
} from "./data-import-form";
import { DataImportResults } from "./data-import-results";
import { errorAlertMessageFrom } from "../../error";

import styles from "./data-import.css";
import { DataSchemaModalContainer } from "./reference-data/modal/data-schema-display-modal-container";
import { DATA_IMPORT_SCHEMA_QUERY } from "../../graphql/query/data-import-schema-query";
import {
  DataImportSchema,
  DataImportSchemaVariables,
} from "../../graphql/__generated_types__/DataImportSchema";
import { FileDataImportCompleted_fileDataImportCompleted } from "../../graphql/__generated_types__/FileDataImportCompleted";
import { useDataImportSubscription } from "./reference-data/hooks/use-data-import-subscriptions";
import { VpdDataImportType } from "../../graphql/__generated_types__/globalTypes";
import { Spin } from "antd";

const { ["container"]: containerClassName } = styles as any;

export type DataImportLabels = {
  importButtonLabel?: string;
  importHintText?: string;
};

export type DataImportProps = {
  onClickAddNew?: () => void;
  importType: VpdDataImportType;
  fetchQuery?: DocumentNode;
  labels: DataImportLabels;
  renderContent: () => ReactNode;
  accept?: FileFormat[];
  loading?: boolean;
  isLibraryScope: boolean;
};

export type ImportStateName =
  | "select-file"
  | "importing"
  | "import-error"
  | "import-failure"
  | "view-libary-imported"
  | "import-success";

export type ImportState = {
  name: ImportStateName;
  errorAlertMessage?: AlertMessage;
  results?: DataFrameImportResult[];
};
const INITIAL_STATE: ImportState = { name: "select-file" };

export const DataImport = ({
  onClickAddNew,
  importType,
  fetchQuery,
  labels,
  renderContent,
  loading,
  isLibraryScope,
}: DataImportProps) => {
  const { importButtonLabel, importHintText } = labels || {};
  const [state, setState] = useState<ImportState>(INITIAL_STATE);
  const { name, results, errorAlertMessage } = state;
  const [showModal, setShowModal] = useState<boolean>(false);

  useEffect(() => {
    if (isLibraryScope) {
      return setState({ ...INITIAL_STATE, name: "view-libary-imported" });
    }
  }, [importType, isLibraryScope]);

  const { data: schemaData, loading: schemaLoading } = useQuery<
    DataImportSchema,
    DataImportSchemaVariables
  >(DATA_IMPORT_SCHEMA_QUERY, {
    variables: { type: importType },
  });

  const [importResults, setImportResults] =
    useState<FileDataImportCompleted_fileDataImportCompleted>();

  const { progress } = useDataImportSubscription((completed) => setImportResults(completed));

  const onViewImportedLibaryContent = () =>
    setState({ ...INITIAL_STATE, name: "view-libary-imported" });
  const onClickCancel = () => setState(INITIAL_STATE);
  const onClickRetry = () => setState({ ...INITIAL_STATE, name: "select-file" });
  const onClickFinish = () => setState(INITIAL_STATE);
  const onImportStarted = () => {
    setImportResults(undefined);
    setState({ ...state, name: "importing" });
  };
  const onImportCompleted = (result: DataImportResult) => {
    if (Array.isArray(result)) {
      setState({
        ...state,
        results: result,
        name: result.some((r) => !r.succeeded) ? "import-failure" : "import-success",
      });
    } else {
      setState({
        ...state,
        errorAlertMessage: errorAlertMessageFrom(result),
        name: "import-error",
      });
    }
  };

  return (
    <>
      <div className={containerClassName}>
        {
          <header>
            <DataImportHeader
              importState={state}
              islibaryScope={isLibraryScope}
              onViewImportedLibrary={onViewImportedLibaryContent}
              schemaLoading={schemaLoading || false}
              onShowSchema={() => setShowModal(true)}
              onClickAddNew={onClickAddNew}
              onImportData={() => onClickRetry()}
              onClickCancel={
                name === "select-file" || name === "import-error" || name === "import-failure"
                  ? onClickCancel
                  : undefined
              }
              onClickRetry={
                name === "import-error" || name === "import-failure" ? onClickRetry : undefined
              }
              onClickFinish={name === "import-success" ? onClickFinish : undefined}
              labels={{ importButtonLabel }}
            />
          </header>
        }
        <>
          {["view-imported", "select-file"].includes(name) && (
            <DataImportForm
              fetchQuery={fetchQuery}
              importType={importType}
              onImportStarted={onImportStarted}
              onImportCompleted={onImportCompleted}
              labels={{ importHintText }}
            />
          )}
          {name === "import-error" && errorAlertMessage && (
            <AlertMessageBanner {...errorAlertMessage} />
          )}
          {isLibraryScope && name === "view-libary-imported" && (
            <VpdScrollable>
              <main>{loading ? <Spin /> : renderContent()}</main>
            </VpdScrollable>
          )}
          {(name === "import-failure" || name === "import-success" || name === "importing") && (
            <DataImportResults
              importingState={state.name}
              importResults={importResults}
              progress={progress}
              results={results || []}
            />
          )}
        </>
      </div>
      {showModal && importType && schemaData && (
        <DataSchemaModalContainer
          handleClose={() => setShowModal(false)}
          schema={schemaData}
          importTypeName={importType}
        />
      )}
    </>
  );
};
